import type { DashboardApplication, DashboardJobWorkload } from "@cv-screener/shared";

export interface ReportPreviewFilters {
  jobIds: string[];
  statuses: string[];
  scoreRanges: Array<{ min: number; max: number }>;
}

export interface ReportPreview {
  matchedApplications: number;
  averageScore: number | null;
  highFitCount: number;
  highFitRate: number | null;
  leadingStatus: { label: string; value: number } | null;
  completionRate: number | null;
  openWork: number;
  errorRate: number | null;
  highestCompletionJob: { jobId: string; processedApplications: number; receivedApplications: number; completionRate: number } | null;
  pipeline: Array<{ label: string; value: number; tone: "green" | "blue" | "violet" | "amber" | "red" }>;
  pipelineByJob: DashboardJobWorkload[];
  scoreDistribution: Array<{ label: string; value: number; tone: "green" | "blue" | "amber" | "red" }>;
  statusDistribution: Array<{ label: string; value: number }>;
}

export type PipelineTrendGranularity = "daily" | "weekly" | "monthly" | "yearly";

export interface PipelineTrendDateRange {
  startDate: string;
  endDate: string;
}

export type ProcessingHistoryRecord = Pick<DashboardApplication, "jobId" | "appliedAt" | "updatedAt" | "aiState">;

export interface PipelineTrendPoint {
  periodStart: string;
  label: string;
  receivedApplications: number;
  processedApplications: number;
  processingApplications: number;
  pendingApplications: number;
  errorApplications: number;
}

const pipelineTrendWindows: Record<PipelineTrendGranularity, number> = {
  daily: 14,
  weekly: 12,
  monthly: 12,
  yearly: 5,
};

function periodStart(date: Date, granularity: PipelineTrendGranularity): Date {
  if (granularity === "daily") return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  if (granularity === "weekly") {
    const day = date.getUTCDay();
    const offset = day === 0 ? 6 : day - 1;
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() - offset));
  }
  if (granularity === "monthly") return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
  return new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
}

function shiftPeriod(date: Date, amount: number, granularity: PipelineTrendGranularity): Date {
  if (granularity === "daily") return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + amount));
  if (granularity === "weekly") return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + amount * 7));
  if (granularity === "monthly") return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + amount, 1));
  return new Date(Date.UTC(date.getUTCFullYear() + amount, 0, 1));
}

function periodLabel(date: Date, granularity: PipelineTrendGranularity): string {
  if (granularity === "yearly") return String(date.getUTCFullYear());
  if (granularity === "monthly") return date.toLocaleDateString("en-GB", { month: "short", year: "numeric", timeZone: "UTC" });
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
}

export function defaultPipelineTrendDateRange(granularity: PipelineTrendGranularity, referenceDate = new Date()): PipelineTrendDateRange {
  const lastPeriod = periodStart(referenceDate, granularity);
  const firstPeriod = shiftPeriod(lastPeriod, 1 - pipelineTrendWindows[granularity], granularity);
  return { startDate: firstPeriod.toISOString().slice(0, 10), endDate: referenceDate.toISOString().slice(0, 10) };
}

export function buildPipelineTrend(records: ProcessingHistoryRecord[], granularity: PipelineTrendGranularity, jobIds: string[], referenceDate = new Date(), dateRange = defaultPipelineTrendDateRange(granularity, referenceDate)): PipelineTrendPoint[] {
  const selectedJobs = new Set(jobIds.map((jobId) => jobId.toUpperCase()));
  const firstPeriod = periodStart(new Date(`${dateRange.startDate}T00:00:00.000Z`), granularity);
  const lastPeriod = periodStart(new Date(`${dateRange.endDate}T00:00:00.000Z`), granularity);
  const periods: Date[] = [];
  for (let period = firstPeriod; period <= lastPeriod; period = shiftPeriod(period, 1, granularity)) periods.push(period);
  const points = new Map(periods.map((date) => {
    const periodStartValue = date.toISOString();
    return [periodStartValue, {
      periodStart: periodStartValue,
      label: periodLabel(date, granularity),
      receivedApplications: 0,
      processedApplications: 0,
      processingApplications: 0,
      pendingApplications: 0,
      errorApplications: 0,
    } satisfies PipelineTrendPoint];
  }));
  const pointFor = (value: string) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return undefined;
    return points.get(periodStart(parsed, granularity).toISOString());
  };

  records.forEach((record) => {
    if (selectedJobs.size > 0 && !selectedJobs.has(record.jobId.toUpperCase())) return;
    const receivedPoint = pointFor(record.appliedAt);
    if (receivedPoint) receivedPoint.receivedApplications += 1;
    const outcomePoint = pointFor(record.updatedAt || record.appliedAt);
    if (!outcomePoint) return;
    if (record.aiState === "Completed" || record.aiState === "Outdated") outcomePoint.processedApplications += 1;
    if (record.aiState === "Processing") outcomePoint.processingApplications += 1;
    if (record.aiState === "Pending") outcomePoint.pendingApplications += 1;
    if (record.aiState === "Failed") outcomePoint.errorApplications += 1;
  });

  return [...points.values()];
}

const scoreBuckets = [
  { label: "85–100", min: 85, max: 101, tone: "green" as const },
  { label: "70–84.9", min: 70, max: 85, tone: "blue" as const },
  { label: "50–69.9", min: 50, max: 70, tone: "amber" as const },
  { label: "0–49.9", min: 0, max: 50, tone: "red" as const },
];

function percentage(numerator: number, denominator: number): number | null {
  return denominator > 0 ? Math.round(numerator / denominator * 1000) / 10 : null;
}

export function buildReportPreview(applications: DashboardApplication[], workloads: DashboardJobWorkload[], filters: ReportPreviewFilters): ReportPreview {
  const selectedJobs = new Set(filters.jobIds.map((jobId) => jobId.toUpperCase()));
  const filteredApplications = applications.filter((application) => {
    const jobMatch = selectedJobs.size === 0 || selectedJobs.has(application.jobId.toUpperCase());
    const statusMatch = filters.statuses.length === 0 || filters.statuses.includes(application.status);
    const scoreMatch = filters.scoreRanges.length === 0 || filters.scoreRanges.some((range) => application.overallScore >= range.min && application.overallScore < range.max);
    return jobMatch && statusMatch && scoreMatch;
  });
  const selectedWorkloads = workloads.filter((workload) => selectedJobs.size === 0 || selectedJobs.has(workload.jobId.toUpperCase()));
  const workloadTotal = (key: keyof Omit<DashboardJobWorkload, "jobId">) => selectedWorkloads.reduce((total, workload) => total + workload[key], 0);
  const statusCounts = new Map<string, number>();
  filteredApplications.forEach((application) => statusCounts.set(application.status, (statusCounts.get(application.status) ?? 0) + 1));
  const statusDistribution = [...statusCounts.entries()].map(([label, value]) => ({ label, value })).sort((left, right) => right.value - left.value || left.label.localeCompare(right.label));
  const highFitCount = filteredApplications.filter((application) => application.overallScore >= 85).length;
  const receivedApplications = workloadTotal("receivedApplications");
  const processedApplications = workloadTotal("processedApplications");
  const errorApplications = workloadTotal("errorApplications");
  const highestCompletionJob = [...selectedWorkloads]
    .filter((workload) => workload.receivedApplications > 0)
    .sort((left, right) => {
      const ratioOrder = right.processedApplications * left.receivedApplications - left.processedApplications * right.receivedApplications;
      return ratioOrder || right.processedApplications - left.processedApplications || left.jobId.localeCompare(right.jobId);
    })[0];

  return {
    matchedApplications: filteredApplications.length,
    averageScore: filteredApplications.length ? Math.round(filteredApplications.reduce((total, application) => total + application.overallScore, 0) / filteredApplications.length * 10) / 10 : null,
    highFitCount,
    highFitRate: percentage(highFitCount, filteredApplications.length),
    leadingStatus: statusDistribution[0] ?? null,
    completionRate: percentage(processedApplications, receivedApplications),
    openWork: workloadTotal("processingApplications") + workloadTotal("pendingApplications"),
    errorRate: percentage(errorApplications, receivedApplications),
    highestCompletionJob: highestCompletionJob ? {
      jobId: highestCompletionJob.jobId,
      processedApplications: highestCompletionJob.processedApplications,
      receivedApplications: highestCompletionJob.receivedApplications,
      completionRate: percentage(highestCompletionJob.processedApplications, highestCompletionJob.receivedApplications) ?? 0,
    } : null,
    pipeline: [
      { label: "Received", value: workloadTotal("receivedApplications"), tone: "green" },
      { label: "AI Processed", value: workloadTotal("processedApplications"), tone: "blue" },
      { label: "Processing", value: workloadTotal("processingApplications"), tone: "violet" },
      { label: "Pending", value: workloadTotal("pendingApplications"), tone: "amber" },
      { label: "Errors", value: workloadTotal("errorApplications"), tone: "red" },
    ],
    pipelineByJob: selectedWorkloads,
    scoreDistribution: scoreBuckets.map((bucket) => ({ label: bucket.label, value: filteredApplications.filter((application) => application.overallScore >= bucket.min && application.overallScore < bucket.max).length, tone: bucket.tone })),
    statusDistribution,
  };
}
